import { Collapse, Tag } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import classNames from "classnames";
import styles from "./index.less";
import {
  DndContext,
  useSensor,
  PointerSensor,
  useSensors,
  DragEndEvent,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSS } from "@dnd-kit/utilities";
import ArrowRightIcon from "../assets/arrow-drop-right-line.png";

export enum ResourceType {
  MENU = "MENU",
  PAGE = "PAGE",
  ELEMENT = "ELEMENT",
}

export type ResourceItem = {
  id: string;
  type: ResourceType;
  name: string;
  path: string;
  icon: string;
  hideInMenu: boolean;
  order: number;
  code: string;
  parentCode?: string;
  children: ResourceItem[];
};

export type NodeOrder = {
  code: string;
  order: number;
  type: ResourceType;
};

const SortableItem = ({ children, id, disabled }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({ id });
  const style = useMemo(() => {
    return {
      transform: CSS.Translate.toString(transform),
      transition,
    };
  }, [transform, transition]);
  return (
    <div
      className={styles["drag-item"]}
      style={style}
      ref={setNodeRef}
      {...attributes}
    >
      {!disabled && (
        <div className={styles["drag-item-icon"]}>
          <MenuOutlined
            ref={setActivatorNodeRef}
            {...listeners}
            style={{ cursor: "grab" }}
          />
        </div>
      )}
      <div className={styles["drag-item-content"]}>{children}</div>
    </div>
  );
};

type ResourceMenuProps = {
  item: ResourceItem;
  toolRender?: (item: ResourceItem) => React.ReactNode;
  disabledDrag?: boolean;
  showNodeType?: boolean;
  onDragEnd?: (item: NodeOrder[]) => void;
  elementPermissionBeforeRender?: (item: ResourceItem) => React.ReactNode;
  elementPermissionAfterRender?: (item: ResourceItem) => React.ReactNode;
};
const ResourceMenu = (props: ResourceMenuProps) => {
  const {
    toolRender,
    item,
    disabledDrag,
    showNodeType,
    onDragEnd,
    elementPermissionBeforeRender,
    elementPermissionAfterRender,
  } = props;
  const [activeKey, setActiveKey] = useState<string[]>([]);
  const firstRender = useRef(true);
  const sensors = useSensors(useSensor(PointerSensor));
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = item.children?.findIndex(
        (item) => item.id === active.id
      );
      const newIndex = item.children?.findIndex((item) => item.id === over?.id);
      const newList = arrayMove(item.children, oldIndex, newIndex);
      onDragEnd?.(
        newList.map((item, index) => {
          return {
            code: item.code,
            order: index,
            type: item.type,
          };
        })
      );
    }
  };

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setActiveKey(item.children && item.children.length > 0 ? ["1"] : []);
  }, [item.children?.length]);

  return (
    <Collapse
      activeKey={activeKey}
      onChange={(key) => {
        setActiveKey(key);
      }}
      size="small"
      rootClassName={styles["resource-menu"]}
      expandIcon={({ isActive }) => {
        return (
          <img
            src={ArrowRightIcon}
            alt="arrow-icon"
            className={classNames(styles["arrow-icon"], {
              [styles["arrow-icon-active"]]: isActive,
            })}
          />
        );
      }}
      items={[
        {
          key: "1",
          label: (
            <>
              {item.name}
              {showNodeType && (
                <Tag color="blue" style={{ marginLeft: 10 }}>
                  菜单
                </Tag>
              )}
            </>
          ),
          extra: toolRender ? toolRender(item) : null,
          children: (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToParentElement, restrictToVerticalAxis]}
            >
              <SortableContext
                items={item?.children?.map((item) => item.id) || []}
                strategy={verticalListSortingStrategy}
                disabled={disabledDrag}
              >
                {item?.children?.map((child) => {
                  return (
                    <SortableItem
                      key={child.id}
                      id={child.id}
                      disabled={disabledDrag}
                    >
                      <ResourcePage
                        item={child}
                        toolRender={toolRender}
                        elementPermissionBeforeRender={
                          elementPermissionBeforeRender
                        }
                        elementPermissionAfterRender={
                          elementPermissionAfterRender
                        }
                      />
                    </SortableItem>
                  );
                })}
              </SortableContext>
            </DndContext>
          ),
        },
      ]}
    />
  );
};
type ResourcePageProps = {
  item: ResourceItem;
  showNodeType?: boolean;
  toolRender?: (item: ResourceItem) => React.ReactNode;
  // 页面元素权限前面部分渲染函数
  elementPermissionBeforeRender?: (item: ResourceItem) => React.ReactNode;
  // 页面元素权限后面部分渲染函数
  elementPermissionAfterRender?: (item: ResourceItem) => React.ReactNode;
};
const ResourcePage = (props: ResourcePageProps) => {
  const {
    item,
    showNodeType,
    toolRender,
    elementPermissionBeforeRender,
    elementPermissionAfterRender,
  } = props;
  const [activeKey, setActiveKey] = useState<string[]>();
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setActiveKey(item.children && item.children.length > 0 ? ["1"] : []);
  }, [item.children?.length]);

  return (
    <Collapse
      size="small"
      rootClassName={classNames(styles["resource-page"], {
        [styles["no-children"]]: !item.children || item.children.length === 0,
      })}
      expandIcon={({ isActive }) => {
        return item.children && item.children.length > 0 ? (
          <img
            src={ArrowRightIcon}
            alt="arrow-icon"
            className={classNames(styles["arrow-icon"], {
              [styles["arrow-icon-active"]]: isActive,
            })}
          />
        ) : (
          <div
            className="w-[12px] h-[100%]"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        );
      }}
      collapsible={
        item.children && item.children.length > 0 ? "header" : "icon"
      }
      activeKey={activeKey}
      items={[
        {
          key: "1",
          label: (
            <>
              {item.name}
              {showNodeType && (
                <Tag color="green" style={{ marginLeft: 10 }}>
                  页面
                </Tag>
              )}
            </>
          ),
          extra: toolRender ? toolRender(item) : null,
          children: item.children ? (
            <div className={styles["element-permission-list"]}>
              {item.children.map((child) => {
                return (
                  <div
                    className={styles["element-permission-item"]}
                    key={child.id}
                  >
                    {elementPermissionBeforeRender?.(child)}
                    {child.name}
                    {elementPermissionAfterRender?.(child)}
                  </div>
                );
              })}
            </div>
          ) : null,
        },
      ]}
      onChange={(key) => {
        setActiveKey(key);
      }}
    />
  );
};

type ResourceManagementProps = {
  resourceList: ResourceItem[];
  toolRender?: (
    item: ResourceItem,
    index: number,
    list: ResourceItem[]
  ) => React.ReactNode;
  disabledDrag?: boolean;
  showNodeType?: boolean;
  onDragEnd?: (item: NodeOrder[]) => void;
  elementPermissionBeforeRender?: (item: ResourceItem) => React.ReactNode;
  elementPermissionAfterRender?: (item: ResourceItem) => React.ReactNode;
};
export const ResourceManagement = (props: ResourceManagementProps) => {
  const {
    resourceList,
    toolRender,
    disabledDrag,
    showNodeType,
    onDragEnd,
    elementPermissionBeforeRender,
    elementPermissionAfterRender,
  } = props;
  const sensors = useSensors(useSensor(PointerSensor));
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = resourceList.findIndex((item) => item.id === active.id);
      const newIndex = resourceList.findIndex((item) => item.id === over?.id);
      const newList = arrayMove(resourceList, oldIndex, newIndex);
      onDragEnd?.(
        newList.map((item, index) => {
          return {
            code: item.code,
            order: index,
            type: item.type,
          };
        })
      );
    }
  };
  return (
    <div className={styles["resource-management"]}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement, restrictToVerticalAxis]}
      >
        <SortableContext
          items={resourceList?.map((item) => item.id) || []}
          strategy={verticalListSortingStrategy}
          disabled={disabledDrag}
        >
          {resourceList?.map((item, index) => {
            if (item.type === ResourceType.MENU) {
              return (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  disabled={disabledDrag}
                >
                  <ResourceMenu
                    item={item}
                    toolRender={(item) =>
                      toolRender?.(item, index, resourceList)
                    }
                    showNodeType={showNodeType}
                    onDragEnd={onDragEnd}
                    disabledDrag={disabledDrag}
                    elementPermissionBeforeRender={
                      elementPermissionBeforeRender
                    }
                    elementPermissionAfterRender={elementPermissionAfterRender}
                  />
                </SortableItem>
              );
            }
            if (item.type === ResourceType.PAGE) {
              return (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  disabled={disabledDrag}
                >
                  <ResourcePage
                    item={item}
                    key={item.id}
                    showNodeType={showNodeType}
                    toolRender={(item) =>
                      toolRender?.(item, index, resourceList)
                    }
                    elementPermissionBeforeRender={
                      elementPermissionBeforeRender
                    }
                    elementPermissionAfterRender={elementPermissionAfterRender}
                  />
                </SortableItem>
              );
            }
            return null;
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
};
